/**
 * The one look every mindspace filter control shares.
 *
 * The panels used to say "selected" three ways: lesson checkboxes in indigo,
 * tag chips in a pale brand tint, and time pills where the UNSELECTED ones
 * were the filled-looking ones (the selected "All time" read as a link). Now
 * selected is the theme's primary fill with a check, as the app's other
 * single-choice controls draw it (Graph | List, the period toggles), and
 * unselected is an outline on the card.
 */

import { cn } from '@ai-tutor/ui';

export type ChipSize = 'sm' | 'touch';

const BASE =
  'inline-flex items-center gap-1 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card';

const SIZE: Record<ChipSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  // The phone sheet: drawn 36px, with a 44px tap area.
  touch: 'tap-target h-9 gap-1.5 px-3 text-sm',
};

const ON = 'border-primary bg-primary font-medium text-primary-foreground';
const OFF = 'border-border bg-card text-foreground hover:bg-muted';
// A tag that will not narrow the current view: the chrome recedes to a dashed
// outline, never the text (see `TagChipPicker`'s note on contrast). Not a
// fill - a tinted fill read as a third state between on and off.
const DIM =
  'border-dashed border-border bg-transparent text-muted-foreground hover:bg-muted';

export function filterChip({
  on,
  dim = false,
  size = 'sm',
}: {
  on: boolean;
  dim?: boolean;
  size?: ChipSize;
}) {
  return cn(BASE, SIZE[size], on ? ON : dim ? DIM : OFF);
}

/** A count inside a chip, subordinate to its label by hue, not by alpha. */
export const chipCount = (on: boolean) =>
  cn(
    'tabular-nums font-normal',
    on ? 'text-primary-foreground/80' : 'text-muted-foreground'
  );
